import { NgModule, provideBrowserGlobalErrorListeners } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';

import { AppRoutingModule } from './app-routing-module';
import { App } from './app';
import { Account } from './account/account';
import { ChangePassword } from './auth/change-password/change-password';
import { ConfirmPin } from './auth/confirm-pin/confirm-pin';
import { Login } from './auth/login/login';
import { Register } from './auth/register/register';
import { ForgotPassword } from './auth/forgot-password/forgot-password';
import { ResetPassword } from './auth/reset-password/reset-password';
import { ReactivateAccount } from './auth/reactivate-account/reactivate-account';
import { AccountSecurity } from './account/security/account-security';
import { Header } from './common/pages/header/header';
import { NotAuthorized } from './common/pages/not-authorized/not-authorized';
import { Dashboard } from './dashboard/dashboard';
import { Home } from './home/home';
import { Orders } from './orders/orders';
import { Markets } from './markets/markets';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { CoreModule } from './_core/core.module';
import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { CommonModule } from '@angular/common';
import { Deposit } from './deposit/deposit';
import { DepositConfirmation } from './deposit/deposit-confirmation/deposit-confirmation';
import { Wallets } from './wallets/wallets';
import { ManageWallets } from './wallets/manage-wallets/manage-wallets';
import { ToastrModule } from 'ngx-toastr';
import { ManageOrder } from './orders/manage-order/manage-order';
import { Countries } from './admin/countries/countries';
import { Currencies } from './admin/currencies/currencies';
import { Users } from './admin/users/users';
import { Settings } from './admin/settings/settings';
import { Pagination } from './common/widget/pagination/pagination';
import { AccountTfa } from './account/tfa/tfa';
import { Tfa } from './auth/tfa/tfa';
import { TwoFactorReminder } from './auth/two-factor-reminder/two-factor-reminder';
import { AdminDropdown } from './common/widget/admin-dropdown/admin-dropdown';
import { AllOrders } from './admin/all-orders/all-orders';
import { AdminKycReviews } from './admin/admin-kyc-reviews/admin-kyc-reviews';
import { AdminKycReviewDetails } from './admin/admin-kyc-review-details/admin-kyc-review-details';
import { Footer } from './common/pages/footer/footer';
import { NgSelectModule } from '@ng-select/ng-select';
import { PaymentCallback } from './payment-callback/payment-callback';
import { PaymentGatewayProviders } from './admin/payment-gateway-providers/payment-gateway-providers';
import { PaymentGatewayConfigs } from './admin/payment-gateway-configs/payment-gateway-configs';
import { PaymentGatewayMappings } from './admin/payment-gateway-mappings/payment-gateway-mappings';
import { WithdrawalForm } from './withdrawal/withdrawal-form/withdrawal-form';
import { WithdrawalConfirm } from './withdrawal/withdrawal-confirm/withdrawal-confirm';
import { WithdrawalHistory } from './withdrawal/withdrawal-history/withdrawal-history';
import { WithdrawalGatewayMappings } from './admin/withdrawal-gateway-mappings/withdrawal-gateway-mappings';
import { WithdrawalPayoutFields } from './admin/withdrawal-payout-fields/withdrawal-payout-fields';
import { WithdrawalRequests } from './admin/withdrawal-requests/withdrawal-requests';
import { WithdrawalDetail } from './admin/withdrawal-detail/withdrawal-detail';
import { WithdrawalBeneficiaries } from './admin/withdrawal-beneficiaries/withdrawal-beneficiaries';
import { MyKycApplications } from './account/compliance/my-kyc-applications/my-kyc-applications';
import { MyKycApplicationDetails } from './account/compliance/my-kyc-application-details/my-kyc-application-details';
import { ComplianceWizard } from './account/compliance/compliance-wizard/compliance-wizard';
import { ComplianceSuccess } from './account/compliance/compliance-success/compliance-success';
import { ComplianceResubmit } from './account/compliance/compliance-resubmit/compliance-resubmit';
import { AdminKycApplications } from './admin/compliance/admin-kyc-applications/admin-kyc-applications';
import { AdminKycApplicationDetails } from './admin/compliance/admin-kyc-application-details/admin-kyc-application-details';
import { ComplianceDashboard } from './account/compliance/compliance-dashboard/compliance-dashboard';
import { ComplianceFieldsStep } from './common/widget/compliance/compliance-fields-step/compliance-fields-step';
import { ComplianceDocumentsStep } from './common/widget/compliance/compliance-documents-step/compliance-documents-step';
import { ComplianceValidateStep } from './common/widget/compliance/compliance-validate-step/compliance-validate-step';
import { ComplianceReviewStep } from './common/widget/compliance/compliance-review-step/compliance-review-step';
import { AdminJurisdictionsList } from './admin/compliance/admin-jurisdictions-list/admin-jurisdictions-list';
import { AdminJurisdictionForm } from './admin/compliance/admin-jurisdiction-form/admin-jurisdiction-form';
import { AdminTiersList } from './admin/compliance/admin-tiers-list/admin-tiers-list';
import { AdminTierForm } from './admin/compliance/admin-tier-form/admin-tier-form';
import { AdminTierLimits } from './admin/compliance/admin-tier-limits/admin-tier-limits';
import { AdminPoliciesList } from './admin/compliance/admin-policies-list/admin-policies-list';
import { AdminPolicyEditor } from './admin/compliance/admin-policy-editor/admin-policy-editor';
import { AdminPolicyFields } from './admin/compliance/admin-policy-fields/admin-policy-fields';
import { AdminPolicyDocuments } from './admin/compliance/admin-policy-documents/admin-policy-documents';
import { AdminPolicyRules } from './admin/compliance/admin-policy-rules/admin-policy-rules';
import { AdminComplianceSetupGuide } from './admin/compliance/admin-compliance-setup-guide/admin-compliance-setup-guide';
import { AdminComplianceDashboard } from './admin/compliance/admin-compliance-dashboard/admin-compliance-dashboard';
import { CountryChangeRequests } from './admin/country-change-requests/country-change-requests';
import { CountryChangeRequestsDetail } from './admin/country-change-requests-detail/country-change-requests-detail';
import { AdminCountryChange } from './admin/admin-country-change/admin-country-change';
import { WithdrawalSetupGuide } from './admin/withdrawal-setup-guide/withdrawal-setup-guide';
import { UserWithdrawalBeneficiaries } from './withdrawal/withdrawal-beneficiaries/withdrawal-beneficiaries';
import { WithdrawalDetailsModal } from './common/widget/withdrawal-details-modal/withdrawal-details-modal';
import { DepositDetailsModal } from './common/widget/deposit-details-modal/deposit-details-modal';
import { AccountPinPad } from './common/widget/account-pin-pad/account-pin-pad';
import { DepositHistory } from './deposit/deposit-history/deposit-history';
import { ComplianceFlags } from './admin/compliance-flags/compliance-flags';
import { HelpCenter } from './support/help-center/help-center';
import { SupportWidget } from './common/widget/support-widget/support-widget';
import { Support } from './support/support';
import { SupportTickets } from './support/tickets/support-tickets/support-tickets';
import { SupportTicketsDetails } from './support/tickets/support-tickets-details/support-tickets-details';
import { AdminSupportTickets } from './admin/support-tickets/admin-support-tickets/admin-support-tickets';
import { AdminSupportTicketDetail } from './admin/support-tickets/admin-support-ticket-detail/admin-support-ticket-detail';
import { SupportContextCard } from './common/support-context-card/support-context-card';
import { PaymentWebhookLogs } from './admin/payment-webhook-logs/payment-webhook-logs';
import { PreventNumberWheelDirective } from './common/directives/prevent-number-wheel.directive';
import { SmartAmountPipe } from './common/pipes/smart-amount.pipe';
import { AdminFinanceDashboard } from './admin/finance/admin-finance-dashboard/admin-finance-dashboard';
import { AdminHouseManagement } from './admin/house-management/admin-house-management';
import { AdminHouseMarketMaker } from './admin/house-market-maker/admin-house-market-maker';
import { BaseChartDirective, provideCharts, withDefaultRegisterables } from 'ng2-charts';
import { AdminMachineLearningDashboard } from './admin/machine-learning/admin-machine-learning-dashboard/admin-machine-learning-dashboard';
import { AdminMachineLearningNav } from './admin/machine-learning/admin-machine-learning-nav/admin-machine-learning-nav';
import { AdminMlPredictions } from './admin/machine-learning/admin-ml-predictions/admin-ml-predictions';
import { AdminMlWithdrawalRisk } from './admin/machine-learning/admin-ml-withdrawal-risk/admin-ml-withdrawal-risk';
import { AdminMlDepositRisk } from './admin/machine-learning/admin-ml-deposit-risk/admin-ml-deposit-risk';
import { AdminMlLiquidity } from './admin/machine-learning/admin-ml-liquidity/admin-ml-liquidity';
import { AdminMlDeploymentHealth } from './admin/machine-learning/admin-ml-deployment-health/admin-ml-deployment-health';
import { AdminMlTraining } from './admin/machine-learning/admin-ml-training/admin-ml-training';
import { AdminMlGovernance } from './admin/machine-learning/admin-ml-governance/admin-ml-governance';
import { AdminMlDeploymentControls } from './admin/machine-learning/admin-ml-deployment-controls/admin-ml-deployment-controls';
import { AdminMlMatching } from './admin/machine-learning/admin-ml-matching/admin-ml-matching';
import { AdminMlOperationalReliability } from './admin/machine-learning/admin-ml-operational-reliability/admin-ml-operational-reliability';
import { AdminMlEvaluation } from './admin/machine-learning/admin-ml-evaluation/admin-ml-evaluation';
import { AdminMlPageGuide } from './admin/machine-learning/admin-ml-page-guide/admin-ml-page-guide';
import { AdditionalDocumentList } from './account/additional-documents/additional-document-list/additional-document-list';
import { OrderEntry } from './common/widget/order-entry/order-entry';
import { AdditionalDocumentDetails } from './account/additional-documents/additional-document-details/additional-document-details';
import { UserNotifications } from './account/notifications/user-notifications/user-notifications';
import { AdminAdditionalDocuments } from './admin/additional-documents/admin-additional-documents/admin-additional-documents';
import { AdminAdditionalDocumentDetails } from './admin/additional-documents/admin-additional-document-details/admin-additional-document-details';

@NgModule({
  declarations: [
    App,
    Account,
    ChangePassword,
    ConfirmPin,
    Login,
    Register,
    ForgotPassword,
    ResetPassword,
    ReactivateAccount,
    AccountSecurity,
    Header,
    NotAuthorized,
    Dashboard,
    Home,
    Orders,
    Markets,
    Deposit,
    DepositConfirmation,
    Wallets,
    ManageWallets,
    ManageOrder,
    Countries,
    Currencies,
    Users,
    Settings,
    Pagination,
    AccountTfa,
    Tfa,
    TwoFactorReminder,
    AdminDropdown,
    AllOrders,
    AdminKycReviews,
    AdminKycReviewDetails,
    Footer,
    PaymentCallback,
    PaymentGatewayProviders,
    PaymentGatewayConfigs,
    PaymentGatewayMappings,
    WithdrawalForm,
    WithdrawalConfirm,
    WithdrawalHistory,
    WithdrawalGatewayMappings,
    WithdrawalPayoutFields,
    WithdrawalRequests,
    WithdrawalDetail,
    WithdrawalBeneficiaries,
    UserWithdrawalBeneficiaries,
    MyKycApplications,
    MyKycApplicationDetails,
    ComplianceWizard,
    ComplianceSuccess,
    ComplianceResubmit,
    AdminKycApplications,
    AdminKycApplicationDetails,
    ComplianceDashboard,
    ComplianceFieldsStep,
    ComplianceDocumentsStep,
    ComplianceValidateStep,
    ComplianceReviewStep,
    AdminJurisdictionsList,
    AdminJurisdictionForm,
    AdminTiersList,
    AdminTierForm,
    AdminTierLimits,
    AdminPoliciesList,
    AdminPolicyEditor,
    AdminPolicyFields,
    AdminPolicyDocuments,
    AdminPolicyRules,
    AdminComplianceSetupGuide,
    AdminComplianceDashboard,
    CountryChangeRequests,
    CountryChangeRequestsDetail,
    AdminCountryChange,
    WithdrawalSetupGuide,
    WithdrawalDetailsModal,
    DepositDetailsModal,
    AccountPinPad,
    DepositHistory,
    ComplianceFlags,
    HelpCenter,
    SupportWidget,
    Support,
    SupportTickets,
    SupportTicketsDetails,
    AdminSupportTickets,
    AdminSupportTicketDetail,
    SupportContextCard,
    PaymentWebhookLogs,
    PreventNumberWheelDirective,
    SmartAmountPipe,
    AdminFinanceDashboard,
    AdminHouseManagement,
    AdminHouseMarketMaker,
    AdminMachineLearningDashboard,
    AdminMachineLearningNav,
    AdminMlPredictions,
    AdminMlWithdrawalRisk,
    AdminMlDepositRisk,
    AdminMlLiquidity,
    AdminMlDeploymentHealth,
    AdminMlTraining,
    AdminMlGovernance,
    AdminMlDeploymentControls,
    AdminMlMatching,
    AdminMlOperationalReliability,
    AdminMlEvaluation,
    AdminMlPageGuide,
    AdminAdditionalDocumentDetails,
    AdminAdditionalDocuments,
    UserNotifications,
    AdditionalDocumentDetails,
    AdditionalDocumentList,
    OrderEntry,
  ],
  imports: [
    CommonModule,
    BrowserModule,
    AppRoutingModule,
    BaseChartDirective,
    FormsModule,
    CoreModule,
    ReactiveFormsModule,
    RouterModule,
    NgSelectModule,
    ToastrModule.forRoot(), // ToastrModule added
  ],
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideHttpClient(withInterceptorsFromDi())
  ],
  bootstrap: [App]
})
export class AppModule { }
